import { useRef, useEffect, useState } from 'react'
import $ from 'jquery'
import 'jquery.ripples'
import { min } from 'three/nodes';

// Extend jQuery interface to include ripples method
declare global {
  interface JQuery {
    ripples(options?: any): JQuery;
    ripples(method: string, ...args: any[]): JQuery;
  }
}

// Main Background component with custom water ripple effect
const Background3D = () => {
  const rippleRef = useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const ripplesInstanceRef = useRef<any>(null)
  const lastMouseMoveRef = useRef(0)
  const [ripplesEnabled, setRipplesEnabled] = useState(true)
  const ripplesEnabledRef = useRef(true)

  // Reference width for normalization (M3 MacBook Air CSS width)
  const REFERENCE_WIDTH = 1750;
  const getScale = () => window.innerWidth / REFERENCE_WIDTH;

  // Frame rate detection
  const [fpsScale, setFpsScale] = useState(1);
  useEffect(() => {
    let last = performance.now();
    let frames = 0;
    let rafId: number;
    function measure(now: number) {
      frames++;
      if (now - last >= 1000) {
        const fps = frames;
        setFpsScale(60 / Math.max(1, fps)); // 60Hz = 1, 120Hz = 0.5, etc.
        frames = 0;
        last = now;
      }
      rafId = requestAnimationFrame(measure);
    }
    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    ripplesEnabledRef.current = ripplesEnabled
  }, [ripplesEnabled])

  // Toggle ripplesEnabled with 'r' key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'r' || e.key === 'R') && !e.repeat) {
        setRipplesEnabled((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Listen for custom fish-ripple events
  useEffect(() => {
    const handleFishRipple = (e: Event) => {
      if (ripplesInstanceRef.current) {
        const custom = e as CustomEvent<{ x: number; y: number }>
        const { x, y } = custom.detail
        const actualScale = getScale();
        const scale = Math.min(actualScale, 1);
        const intensityrand = (Math.random()-0.5)*0.01
        ripplesInstanceRef.current.ripples('drop', x, y, 18 * scale * (1/fpsScale), (0.015+intensityrand) * scale * (1/fpsScale))
      }
    }
    window.addEventListener('fish-ripple', handleFishRipple as EventListener)
    return () => {
      window.removeEventListener('fish-ripple', handleFishRipple as EventListener)
    }
  }, [])

  // Event handlers (do not depend on state)
  const handleMouseMove = (e: MouseEvent) => {
    if (!ripplesEnabledRef.current) return
    if (ripplesInstanceRef.current) {
      // Throttle mouse move events to make ripples calmer
      const now = Date.now()
      if (now - lastMouseMoveRef.current > 150 * fpsScale) { // Only create ripples every 100ms
        const x = e.clientX
        const y = e.clientY
        const actualScale = getScale();
        const scale = Math.min(actualScale, 1);
        console.log(fpsScale);
        ripplesInstanceRef.current.ripples('drop', x, y, 15 * scale * (1/fpsScale), 0.008 * scale * (1/fpsScale)) // Smaller, gentler ripples
        lastMouseMoveRef.current = now
      }
    }
  }

  const handleClick = (e: MouseEvent) => {
    if (!ripplesEnabledRef.current) return
    if (ripplesInstanceRef.current) {
      const x = e.clientX
      const y = e.clientY
      const actualScale = getScale();
      const scale = Math.min(actualScale, 1);
      //console.log(scale)
      ripplesInstanceRef.current.ripples('drop', x, y, 25 * scale * (1/fpsScale), 0.1 * scale * (1/fpsScale)) // Gentler click ripples
    }
  }

  useEffect(() => {
    let cleanup: (() => void) | undefined

    // Preload the image first
    const img = new Image()
    img.onload = () => {
      setIsLoaded(true)
      
      if (rippleRef.current) {
        try {
          // Clean up any existing ripples first
          if (ripplesInstanceRef.current) {
            ripplesInstanceRef.current.ripples('destroy')
          }

          const $element = $(rippleRef.current)
          const actualScale = getScale();
          const scale = Math.min(actualScale, 1);
          let res = 1024;
          //console.log(actualScale);
          if (actualScale < 1) {
            res = res * Math.max(0.6, actualScale);
          }
          else {
            res = res * (1+(actualScale-1)*2);
          }
          // Use fpsScale to adjust plugin parameters
          //console.log(fpsScale);
          const dropRadius = 50 * scale * (1/fpsScale);
          const perturbance = 0.05 * scale * (1/fpsScale);
          $element.ripples({
            imageUrl: '/bg.jpg',
            dropRadius,
            perturbance,
            resolution: res,
            interactive: true,
            crossOrigin: ''
          })
          
          // Store the instance
          ripplesInstanceRef.current = $element
          
          // Add event listeners
          document.addEventListener('mousemove', handleMouseMove)
          document.addEventListener('click', handleClick)
          
          // Create initial ripples for a more dynamic start
          setTimeout(() => {
            if (ripplesInstanceRef.current) {
              const actualScale = getScale();
              const scale = Math.min(actualScale, 1);
              // Create a few gentle ripples in different positions
              ripplesInstanceRef.current.ripples('drop', window.innerWidth * 0.5, window.innerHeight * 0.6, 20 * scale * (1/fpsScale), 0.02 * scale * (1/fpsScale))
              setTimeout(() => {
                if (ripplesInstanceRef.current) {
                  ripplesInstanceRef.current.ripples('drop', window.innerWidth * 0.57, window.innerHeight * 0.55, 10 * scale * (1/fpsScale), 0.01 * scale * (1/fpsScale))
                }
              }, 510)
              setTimeout(() => {
                if (ripplesInstanceRef.current) {
                  ripplesInstanceRef.current.ripples('drop', window.innerWidth * 0.22, window.innerHeight * 0.34, 19 * scale * (1/fpsScale), 0.5 * scale * (1/fpsScale))
                }
              }, 800)
              
            }
          }, 500)
          
          // Set up cleanup function
          cleanup = () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('click', handleClick)
            if (ripplesInstanceRef.current) {
              ripplesInstanceRef.current.ripples('destroy')
              ripplesInstanceRef.current = null
            }
          }
        } catch (error) {
          console.error('Error initializing background ripples:', error)
        }
      }
    }
    img.onerror = () => {
      console.error('Failed to load background image')
    }
    img.src = '/bg.jpg'

    // Cleanup function
    return () => {
      if (cleanup) {
        cleanup()
      }
    }
  }, []) // Only run once on mount

  return (
    <div className="fixed inset-0 -z-10">
      {/* Ripple Toggle Indicator (bottom right) */}
      <div
        style={{
          position: 'fixed',
          bottom: 20,
          right: 28,
          zIndex: 100,
          background: 'rgba(255,255,255,0.4)',
          color: ripplesEnabled ? '#6da1bf ' : '#969696 ',
          borderRadius: 8,
          padding: '6px 16px',
          fontWeight: 600,
          fontFamily: 'monospace',
          fontSize: 15,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: ripplesEnabled ? '2px solid #6da1bf' : '2px solid #969696',
          transition: 'all 0.2s',
          pointerEvents: 'none',
        }}
      >
        ripples: {ripplesEnabled ? 'on' : 'off'}
      </div>
      <div 
        ref={rippleRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: isLoaded ? 'transparent' : '#eaf0fa',
          backgroundImage: isLoaded ? 'url(/bg.jpg)' : 'none',
          backgroundSize: 'cover',
          backgroundPosition: '50% 25%',
          backgroundRepeat: 'no-repeat',
        }}
      />
    </div>
  )
}

export default Background3D 